import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Layout, Menu, Typography, Dropdown, Input, Space, Spin } from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    DollarOutlined,
    BankOutlined,
    ShopOutlined,
    WalletOutlined,
    BarChartOutlined,
    SettingOutlined,
    CreditCardOutlined,
    SafetyOutlined,
    ApiOutlined,
    CustomerServiceOutlined,
    MobileOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    SearchOutlined,
    LockOutlined,
    LogoutOutlined,
    QuestionCircleOutlined,
    MessageOutlined,
    UsergroupAddOutlined,
    SecurityScanOutlined,
    DashboardOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import KasakaSiderLogo from '../svg/kasaka_sider';
import KasakaIcon from '../svg/kasaka_icon';
import Footer from './Footer';

const { Header, Sider, Content } = Layout;
const { SubMenu } = Menu;
const { Title } = Typography;

// Lazy load components for children
const ListCouncils = lazy(() => import('../components/ListCouncils'));
const CollectionAgents = lazy(() => import('../components/CollectionAgents'));
const PayerManagement = lazy(() => import('./PayerManagement'));
const LevyManagement = lazy(() => import('../components/LevyManagement'));
const RevenueTracker = lazy(() => import('../components/RevenueTracker'));
const ListMarkets = lazy(() => import('../components/ListMarkets'));
const Marketeers = lazy(() => import('../components/Marketeers'));
const MarketFees = lazy(() => import('../components/MarketFees'));
const UserAccess = lazy(() => import('../components/UserAccess'));
const AuditTrails = lazy(() => import('../components/AuditTrails'));
const ManageWallets = lazy(() => import('../components/ManageWallets'));
const PaymentChannels = lazy(() => import('../components/PaymentChannels'));
const APIAccess = lazy(() => import('../components/APIAccess'));
const ManageDevices = lazy(() => import('../components/ManageDevices'));
const KYCCompliance = lazy(() => import('../components/KYCCompliance'));
const ExecutiveDashboard = lazy(() => import('./ExecutiveDashboard'));

interface MenuItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    permissions: string[];
    children?: MenuItem[];
    component?: React.LazyExoticComponent<React.ComponentType<any>>;
}

const Dashboard: React.FC = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const { user: userData, hasPermission, checkAuth, logout } = useAuth();
    const [greeting, setGreeting] = useState('');
    const isMobileViewRef = useRef(window.innerWidth <= 768);

    const updateGreeting = useCallback(() => {
        const hour = new Date().getHours();
        let greetingText = '';
        if (hour < 12) greetingText = 'Good morning';
        else if (hour < 18) greetingText = 'Good afternoon';
        else greetingText = 'Good evening';

        if (userData && userData.first_name && userData.last_name) {
            setGreeting(`${greetingText}, ${userData.first_name} ${userData.last_name}`);
        } else {
            setGreeting(`${greetingText}, User`);
        }
    }, [userData]);

    useEffect(() => {
        const init = async () => {
            await checkAuth();
            setIsLoading(false);
            setSelectedKey('executive-dashboard');
        };
        init();
    }, [checkAuth]);


    useEffect(() => {
        updateGreeting();
    }, [updateGreeting]);

    useEffect(() => {
        const handleResize = () => {
            isMobileViewRef.current = window.innerWidth <= 768;
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const userMenuItems = [
        { key: 'security', icon: <LockOutlined />, label: 'Security' },
        { key: 'messages', icon: <MessageOutlined />, label: 'Messages' },
        { key: 'support', icon: <QuestionCircleOutlined />, label: 'Request Support' },
        { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout },
    ];

    const menuItems: MenuItem[] = [
        {
            key: 'executive-dashboard',
            icon: <DashboardOutlined />,
            label: 'Home',
            permissions: ['view_executive_dashboard', 'manage_local_authorities'],
            component: ExecutiveDashboard,
        },
        {
            key: 'local-authorities',
            icon: <BankOutlined />,
            label: 'Local Authorities',
            permissions: ['manage_local_authorities'],
            children: [
                { key: 'list-councils', icon: <BankOutlined />, label: 'Councils', permissions: ['manage_agents'], component: ListCouncils },
                { key: 'collection-agents', icon: <TeamOutlined />, label: 'Agents', permissions: ['manage_agents'], component: CollectionAgents },
                { key: 'levy-management', icon: <DollarOutlined />, label: 'Levies & Tarrifs', permissions: ['manage_levies'], component: LevyManagement },
                { key: 'payers', icon: <UsergroupAddOutlined />, label: 'Ratepayers', permissions: ['manage_users'], component: PayerManagement },
                { key: 'revenue-management', icon: <BarChartOutlined />, label: 'Revenue Tracker', permissions: ['manage_revenue'], component: RevenueTracker },
            ],
        },
        {
            key: 'cooperatives',
            icon: <ShopOutlined />,
            label: 'Markets',
            permissions: ['manage_cooperatives'],
            children: [
                { key: 'market-management', icon: <ShopOutlined />, label: 'Markets List', permissions: ['manage_markets'], component: ListMarkets },
                { key: 'marketeers', icon: <UsergroupAddOutlined />, label: 'Marketeers', permissions: ['manage_users'], component: Marketeers },
                { key: 'fee-management', icon: <DollarOutlined />, label: 'Market Fees', permissions: ['manage_levies'], component: MarketFees },
                { key: 'revenue-management-coop', icon: <BarChartOutlined />, label: 'Revenue Tracker', permissions: ['manage_revenue'], component: RevenueTracker },
            ],
        },
        {
            key: 'system-administration',
            icon: <SettingOutlined />,
            label: 'System Control',
            permissions: ['administer_system'],
            children: [
                { key: 'user-access-management', icon: <UserOutlined />, label: 'User Access', permissions: ['manage_users'], component: UserAccess },
                { key: 'audit-trail', icon: <SecurityScanOutlined />, label: 'Audit Trails', permissions: ['manage_devices'], component: AuditTrails },
                { key: 'wallet-management', icon: <WalletOutlined />, label: 'Manage Wallets', permissions: ['manage_wallets'], component: ManageWallets },
                { key: 'payment-management', icon: <CreditCardOutlined />, label: 'Payment Channels', permissions: ['manage_payment_methods'], component: PaymentChannels },
                { key: 'integration-api', icon: <ApiOutlined />, label: 'API Access', permissions: ['manage_integration_settings'], component: APIAccess },
                { key: 'device-management', icon: <MobileOutlined />, label: 'Manage Devices', permissions: ['manage_devices'], component: ManageDevices },
            ],
        },
        {
            key: 'support-feedback',
            icon: <CustomerServiceOutlined />,
            label: 'Support Center',
            permissions: ['access_support_portal'],
            children: [
                { key: 'kyc-compliance', icon: <SafetyOutlined />, label: 'KYC Compliance', permissions: ['perform_kyc_verification'], component: KYCCompliance },
            ],
        },
        {
            key: 'reporting-analytics',
            icon: <BarChartOutlined />,
            label: 'Data Analytics',
            permissions: ['view_reports'],
            component: ExecutiveDashboard,
        },
    ];

    const renderMenuItem = (item: MenuItem) => {
        if (item.children) {
            return (
                <SubMenu key={item.key} icon={item.icon} title={item.label}>
                    {item.children.map(child =>
                        hasPermission(child.permissions) && (
                            <Menu.Item key={child.key} icon={child.icon}>
                                {child.label}
                            </Menu.Item>
                        )
                    )}
                </SubMenu>
            );
        }
        return (
            <Menu.Item key={item.key} icon={item.icon}>
                {item.label}
            </Menu.Item>
        );
    };

    const handleMenuClick = (key: string) => {
        setSelectedKey(key);
    };

    const renderContent = () => {
        const findMenuItem = (items: MenuItem[]): MenuItem | undefined => {
            for (const item of items) {
                if (item.key === selectedKey) return item;
                if (item.children) {
                    const found = item.children.find(child => child.key === selectedKey);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const selectedItem = findMenuItem(menuItems);

        if (selectedItem && selectedItem.component) {
            return (
                <Suspense fallback={<Spin size="large" />}>
                    <selectedItem.component />
                </Suspense>
            );
        }
        return null;
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: 'var(--body-bg)' }}>
            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                theme="light"
                style={{ backgroundColor: '#ffffff' }}
                breakpoint="lg"
                collapsedWidth={80}
                trigger={null}
            >
                <div style={{ padding: '14px', textAlign: 'center' }}>
                    {collapsed ? <KasakaIcon /> : <KasakaSiderLogo />}
                </div>
                <Menu
                    theme="light"
                    defaultSelectedKeys={['1']}
                    mode="inline"
                    style={{ backgroundColor: '#ffffff' }}
                    defaultOpenKeys={menuItems.map(item => item.key)}
                    onClick={({ key }) => handleMenuClick(key)}
                >
                    <Menu.Item
                        key="toggle"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {!collapsed && "Toggle Menu"}
                    </Menu.Item>
                    {menuItems.map(item =>
                        hasPermission(item.permissions) && renderMenuItem(item)
                    )}
                </Menu>
            </Sider>
            <Layout className="site-layout">
                <Header className="site-layout-background" style={{ padding: 0, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {!isMobileViewRef.current && <Title level={4} style={{ margin: '0 10px' }}>
                        {greeting}
                    </Title>}
                    <Space style={{ marginRight: '16px' }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="Search"
                            style={{ width: 200 }}
                        />
                        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                            <span style={{ cursor: 'pointer' }}>
                                <Space>
                                    <UserOutlined />
                                    {isMobileViewRef.current ? null : userData && (
                                        <div>
                                            <span style={{ fontSize: '14px', fontWeight: 'medium' }}>
                                                {`${userData.first_name} ${userData.last_name}`}
                                            </span>
                                        </div>
                                    )}
                                </Space>
                            </span>
                        </Dropdown>
                    </Space>
                </Header>
                <Spin spinning={isLoading} tip="Please wait...">
                    <div style={{ position: 'relative', height: '100%', minHeight: 'calc(100vh - 32px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Content style={{
                            width: '100%',
                            margin: '5px',
                            position: 'relative',
                            minHeight: 'calc(100vh - 32px)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            {renderContent()}
                            <Footer style={{ marginTop: 'auto' }} />
                        </Content>
                    </div>
                </Spin>
            </Layout>
        </Layout>
    );
};

export default Dashboard;


